import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AiGeneration, AiImage } from "@media-viewer/shared";
import { api, ApiError } from "../../api/client";

export function generationMeta(g: AiGeneration): string {
  const parts = [g.modelName];
  for (const k of ["aspectRatio", "resolution", "quality"] as const) {
    const v = g.options[k];
    if (typeof v === "string") parts.push(v);
  }
  const credits = g.status === "pending" ? g.creditsReserved : g.creditsCharged;
  parts.push(`${credits} ${credits === 1 ? "credit" : "credits"}`);
  return parts.join(" · ");
}

export function failureMessage(error: string | null): string {
  switch (error) {
    case "insufficient_balance":
      return "The site's Venice balance is empty — tell the admin";
    case "capacity":
      return "Model busy, try again";
    case "content_policy":
      return "Prompt refused by Venice";
    default:
      return "Generation failed — not charged";
  }
}

function saveErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 410) return "This result has expired and can't be saved";
    if (err.status === 409) return "Blocked results can't be saved";
    if (err.status === 404) return "This result no longer exists";
  }
  return "Couldn't save — try again";
}

function CardShell({ g, children, media }: { g: AiGeneration; children?: ReactNode; media: ReactNode }) {
  return (
    <article className="flex flex-col overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900">
      {media}
      <div className="flex flex-col gap-2 p-3">
        <p className="line-clamp-2 text-sm text-zinc-200" title={g.prompt}>
          {g.prompt}
        </p>
        <span className="text-xs text-zinc-500">{generationMeta(g)}</span>
        {children}
      </div>
    </article>
  );
}

function Placeholder({ children, tone = "text-zinc-400" }: { children: ReactNode; tone?: string }) {
  return (
    <div className={"flex aspect-square flex-col items-center justify-center gap-2 bg-zinc-950 p-4 text-center text-sm " + tone}>
      {children}
    </div>
  );
}

export function PendingCard({ g }: { g: AiGeneration }) {
  return (
    <CardShell
      g={g}
      media={
        <Placeholder>
          <span className="inline-block h-7 w-7 animate-spin rounded-full border-2 border-zinc-500 border-t-transparent" />
          Generating…
        </Placeholder>
      }
    />
  );
}

export function FailedCard({ g }: { g: AiGeneration }) {
  return <CardShell g={g} media={<Placeholder tone="text-rose-300">{failureMessage(g.error)}</Placeholder>} />;
}

interface ResultCardProps {
  g: AiGeneration;
  image: AiImage;
  onDiscarded: (imageId: number) => void;
  onUseAsExample: (image: AiImage) => void;
}

export function ResultCard({ g, image, onDiscarded, onUseAsExample }: ResultCardProps) {
  const qc = useQueryClient();
  const [fileGone, setFileGone] = useState(false);

  const save = useMutation({
    mutationFn: () => api.ai.save(image.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai", "generations"] });
      void qc.invalidateQueries({ queryKey: ["media"] });
    },
  });

  const discard = useMutation({
    mutationFn: () => api.ai.discard(image.id),
    onSuccess: () => {
      onDiscarded(image.id);
      void qc.invalidateQueries({ queryKey: ["ai", "generations"] });
    },
  });

  if (image.status === "refused") {
    return (
      <CardShell
        g={g}
        media={<Placeholder tone="text-amber-300">Blocked by Venice's content filter — not charged</Placeholder>}
      />
    );
  }

  const savedId = save.data?.mediaId ?? image.savedMediaId;
  const duplicate = save.data?.duplicate ?? false;

  const media = fileGone && savedId !== null ? (
    <Placeholder>Saved</Placeholder>
  ) : (
    <a href={image.url} target="_blank" rel="noreferrer" className="block bg-zinc-950">
      <img
        src={image.url}
        alt={g.prompt}
        loading="lazy"
        onError={() => setFileGone(true)}
        className="aspect-square w-full object-contain"
      />
    </a>
  );

  return (
    <CardShell g={g} media={media}>
      {savedId !== null ? (
        <div role="status" className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-emerald-400">{duplicate ? "Already in collection" : "Saved to collection"}</span>
          <a href={`/media/${savedId}`} className="text-sky-300 hover:text-sky-200">
            View image →
          </a>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending || discard.isPending}
              className="rounded bg-emerald-700 px-3 py-1.5 text-sm text-emerald-100 hover:bg-emerald-600 disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save to collection"}
            </button>
            <button
              onClick={() => discard.mutate()}
              disabled={save.isPending || discard.isPending}
              className="rounded bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200 hover:bg-zinc-700 disabled:opacity-50"
            >
              Discard
            </button>
          </div>
          {save.isError && <span className="text-xs text-rose-400">{saveErrorMessage(save.error)}</span>}
          {discard.isError && <span className="text-xs text-rose-400">Couldn't discard — try again</span>}
        </>
      )}
      {savedId === null && (
        <button
          onClick={() => onUseAsExample(image)}
          className="rounded border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
        >
          Use as example image
        </button>
      )}
    </CardShell>
  );
}
